// @flow

import React, { useState, useEffect, useMemo, useRef } from "react";
import { localize } from "redux-i18n";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Col, View } from "@components";
import { SchemaForm } from "@components";
import type { PropertiesSchema } from "@types";
import getUiSchema from "./ui-schema";
import type { EmployeeType } from "@modules/employee/types";
import type { PositionLinkedAbsence } from "@modules/employee/reducer/employment-details/types";
import LinkedAbsences, { isLinkValid } from "./linked-absences";

type Props = {
  onDismissHandler: () => void,
  onSubmit: (form: Object) => void,
  schema: ?PropertiesSchema,
  submitButtonText: string,
  submitButtonIcon: string,
  title: string,
  formData: Object | null,
  employee: ?EmployeeType,
  linkedAbsences?: ?Array<PositionLinkedAbsence>,
  ...PropsFromLocalize,
};

export function AddEditEmployeePositionModal(props: Props) {
  const {
    onDismissHandler,
    onSubmit,
    schema,
    submitButtonText,
    submitButtonIcon,
    title,
    employee,
    linkedAbsences,
    t,
  } = props;

  const formRef = useRef(null);
  const [formData, setFormData] = useState(props.formData);

  useEffect(() => {
    setFormData(props.formData);
  }, [props.formData]);

  const submitForm = () => {
    formRef.current.submit();
  };

  const handleSubmit = (data: Object) => {
    onSubmit(data);
  };

  const handleChange = (data: Object) => {
    setFormData(data);
  };

  const isEditing = !!(props.formData && props.formData.id);

  const { activeFrom, activeTo } = formData || {};
  const hasBlockedAbsences = useMemo(
    () => (linkedAbsences || []).some(absence => !isLinkValid(absence, activeFrom, activeTo)),
    [linkedAbsences, activeFrom, activeTo],
  );

  const uiSchema = useMemo(() => {
    const baseUiSchema = getUiSchema(t);
    return {
      ...baseUiSchema,
      activeFrom: {
        ...baseUiSchema.activeFrom,
        ["ui:options"]: {
          ...baseUiSchema.activeFrom["ui:options"],
          minDate: (employee || {}).dateOfBirth,
        },
      },
      organisationalUnit: {
        ...baseUiSchema.organisationalUnit,
        "ui:options": {
          searchPlaceholder: t("SEARCH_DEPARTMENT"),
          searchable: true,
          inputPlaceholder: t("SELECT_DEPARTMENT"),
          modalTitle: t("SELECT_DEPARTMENT"),
          cancelButtonTitle: t("CANCEL"),
          submitButtonTitle: t("SUBMIT"),
        },
      },
    };
  }, [t, employee]);

  return (
    <Modal className="modal-normal">
      <ModalHeader toggle={onDismissHandler}>{title}</ModalHeader>
      <ModalBody>
        <View gap={4}>
          <SchemaForm
            ref={formRef}
            schema={schema}
            uiSchema={uiSchema}
            formData={formData}
            onSubmit={handleSubmit}
            onChange={handleChange}
            externalSubmit
          />
          {isEditing && (
            <LinkedAbsences
              linkedAbsences={linkedAbsences}
              activeFrom={activeFrom}
              activeTo={activeTo}
            />
          )}
        </View>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={onDismissHandler}>
          {t("CANCEL")}
        </Button>
        <Button materialIcon={submitButtonIcon} onClick={submitForm} disabled={hasBlockedAbsences}>
          {submitButtonText}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default localize()(AddEditEmployeePositionModal);
